import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { getResetPasswordUrl } from '@/utils/url';
import logo from '@/assets/images/logo/logo.svg';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async ({ email }: { email: string }) => {
    setLoading(true);
    try {
      const { data: emailExists, error: checkError } = await supabase.rpc('check_email_exists', { email_to_check: email });
      if (checkError) {
        toast({
          title: "Error",
          description: "Could not check e-mail. Try again.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      if (!emailExists) {
        toast({
          title: "E-mail not found",
          description: "This e-mail is not registered.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      const resetUrl = getResetPasswordUrl();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl,
      });

      if (error) {
        console.error('Password reset error:', error);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        navigate(`/reset-password-sent?email=${encodeURIComponent(email)}`);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit({ email }); }} className="space-y-6">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={onBack}
          className="text-white/80 hover:text-white mr-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-semibold text-white font-sans">Reset Password</h2>
      </div>

      <div className="space-y-4">
        <p className="text-white/80 text-sm">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        
        <Input
          type="email"
          placeholder="E-mail"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailTouched) {
              setEmailError(validateEmail(e.target.value) ? '' : 'Please enter a valid e-mail address');
            }
          }}
          onBlur={() => {
            setEmailTouched(true);
            setEmailError(validateEmail(email) ? '' : 'Please enter a valid e-mail address');
          }}
          className="bg-white/20 backdrop-blur-sm border-2 border-white text-white placeholder:text-white focus:ring-white/30 focus:border-white rounded-lg px-6 py-4 h-14"
          required
        />
        {emailTouched && emailError && (
          <p className="mt-1 text-sm text-white">{emailError}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
      >
        {loading ? 'Sending...' : 'Send Reset Email'}
      </Button>
    </form>
  );
};
